import {phoneLocalToGlobal} from '@yandex-delivery/core/phones/utils'
import {get, getUUID, join, pipe, reverse, split, toDefaultEmptyString} from '@yandex-delivery/utils'

import {i18n} from 'src/shared/config'
import type {CustomParcelSizes, NddClaim, NddClaimDestinationPoint, Order} from 'src/shared/types'

import {fromNddDestinationPoint} from './from-ndd-destination-point'
import {fromNddParcel} from './from-ndd-parcel'
import {fromNddProducts} from './from-ndd-products'
import {fromNddSourcePoint} from './from-ndd-source-point'

export function fromOrderToNddClaim(
  order: Omit<Order, 'nddOrder'>,
  customParcelSizes: CustomParcelSizes,
): Partial<NddClaim> {
  const orderNumber = getUUID()
  const sourcePoint = get('sourcePoint', order)
  const senderPhone = get('contact.phone', sourcePoint)
  const destinationPoint = get('destinationPoints.0', order)
  const deliveryDate = get('ndd.deliveryDate', order)
  const deliveryInterval = get('ndd.deliveryInterval', order)
  const parcels = get('parcels', destinationPoint)
  const paymentMethodId = get('paymentInfo.methodId', order)
  const paymentMethodType = get('paymentInfo.type', order)

  const value = {
    info: {
      operator_request_id: orderNumber,
      comment: get('address.comment', destinationPoint),
    },
    billing_info: {
      delivery_cost: 0,
      payment_method: 'already_paid' as const,
    },
    recipient_info: {
      first_name: pipe(destinationPoint, get('contact.name'), toDefaultEmptyString),
      phone: pipe(destinationPoint, get('contact.phone'), phoneLocalToGlobal, toDefaultEmptyString),
      email: get('contact.email', destinationPoint),
    },
    sender_info: senderPhone ? {phone: senderPhone} : undefined,
    source: fromNddSourcePoint(sourcePoint),
    destination: fromNddDestinationPoint(destinationPoint, deliveryInterval, deliveryDate),
    last_mile_policy: 'self_pickup' as const,
    particular_items_refuse: false,
    items: fromNddProducts(parcels, orderNumber, false),
    places: fromNddParcel(parcels, orderNumber, customParcelSizes),
  }

  if (paymentMethodId && paymentMethodType) {
    Object.assign(value.billing_info, {
      delivery_corp_payment_info: {
        method_id: paymentMethodId,
        type: paymentMethodType,
      },
    })
  }

  return value
}

export function fromOrderToNddClaimMobile(
  order: Omit<Order, 'nddOrder'>,
  customParcelSizes: CustomParcelSizes,
): Partial<NddClaim> {
  const orderNumber = getUUID()
  const route = get('ndd.route', order)
  const sourcePoint = get('source', route)
  const destinationPoint = get('destination', route)
  const destinationPointDefault = get('destinationPoints.0', order)
  const senderPhone = get('phone', sourcePoint)
  const deliveryDate = get('ndd.deliveryDate', order)
  const parcels = get('parcels', destinationPointDefault)
  const paymentMethodId = get('paymentInfo.methodId', order)
  const paymentMethodType = get('paymentInfo.type', order)

  const value = {
    info: {
      operator_request_id: orderNumber,
    },
    billing_info: {
      delivery_cost: 0,
      payment_method: 'already_paid' as const,
    },
    recipient_info: {
      first_name: i18n('cargo.create.destinations.recipient', 'Получатель'),
      phone: pipe(destinationPoint, get('phone'), phoneLocalToGlobal, toDefaultEmptyString),
    },
    sender_info: senderPhone ? {phone: senderPhone} : undefined,
    source: {
      platform_station: {
        platform_id: get('pickupPoint._id', sourcePoint),
      },
    },
    destination: {
      type: 'platform_station' as NddClaimDestinationPoint['type'],
      platform_station: {
        platform_id: get('pickupPoint._id', destinationPoint),
      },
      interval_utc: {
        from: pipe(deliveryDate, split('.'), reverse, join('-')),
        to: pipe(deliveryDate, split('.'), reverse, join('-')),
      },
    },
    last_mile_policy: 'self_pickup' as const,
    particular_items_refuse: false,
    items: fromNddProducts(parcels, orderNumber, false),
    places: fromNddParcel(parcels, orderNumber, customParcelSizes),
  }

  if (paymentMethodId && paymentMethodType) {
    Object.assign(value.billing_info, {
      delivery_corp_payment_info: {
        method_id: paymentMethodId,
        type: paymentMethodType,
      },
    })
  }

  return value
}
